import React, { useState, useEffect, useRef } from "react";
import { getTraces, getTrace, generateTrace } from "@/api/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Network,
  Plus,
  RefreshCw,
  AlertCircle,
  Download,
  Search,
  Filter,
  Maximize2,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

type SpanStatus = "OK" | "ERROR" | string;

interface TraceListItem {
  trace_id: string;
  root_service: string;
  service_count: number;
  cloud_provider: string;
  total_duration_ms?: number;
  spans?: Array<{ status: SpanStatus }>;
}

interface SpanDetail {
  span_id: string;
  trace_id: string;
  service_name: string;
  name: string;
  duration_ms: number;
  status: SpanStatus;
  span_kind: string;
  parent_span_id?: string;
  start_time: string | number;
  end_time: string | number;
  attributes?: Record<string, any>;
  children?: SpanDetail[];
}

interface TraceDetail {
  trace_id: string;
  root_service: string;
  service_count: number;
  cloud_provider: string;
  total_duration_ms?: number;
  spans: SpanDetail[];
}

const TraceVisualization: React.FC = () => {
  const [traces, setTraces] = useState<TraceListItem[]>([]);
  const [selectedTrace, setSelectedTrace] = useState<TraceDetail | null>(null);
  const [selectedSpan, setSelectedSpan] = useState<SpanDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [viewMode, setViewMode] = useState<"graph" | "waterfall" | "table">("graph");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<"all" | "ok" | "error">("all");
  const [showSpanDialog, setShowSpanDialog] = useState<boolean>(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    fetchTraces();
  }, []);

  useEffect(() => {
    if (selectedTrace && viewMode === "graph" && canvasRef.current) {
      drawGraph();
    }
  }, [selectedTrace, viewMode]);

  const fetchTraces = async () => {
    try {
      setLoading(true);
      const data = (await getTraces(50)) as unknown as TraceListItem[];
      setTraces(data);
      if (data.length > 0 && data[0]?.trace_id) {
        await loadTrace(data[0].trace_id);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching traces:", error);
      setLoading(false);
    }
  };

  const loadTrace = async (traceId: string) => {
    try {
      const trace = (await getTrace(traceId)) as unknown as TraceDetail;
      setSelectedTrace(trace);
    } catch (error) {
      console.error("Error loading trace:", error);
    }
  };

  const handleGenerateTrace = async (withAnomaly = false) => {
    try {
      await generateTrace(withAnomaly);
      await fetchTraces();
    } catch (error) {
      console.error("Error generating trace:", error);
    }
  };

  const drawGraph = () => {
    if (!canvasRef.current || !selectedTrace) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Build hierarchy
    const spanMap = new Map<string, SpanDetail & { children: SpanDetail[] }>();
    const rootSpans: (SpanDetail & { children: SpanDetail[] })[] = [];

    selectedTrace.spans.forEach((span) => {
      spanMap.set(span.span_id, { ...span, children: [] });
    });

    selectedTrace.spans.forEach((span) => {
      if (span.parent_span_id && spanMap.has(span.parent_span_id)) {
        const parent = spanMap.get(span.parent_span_id);
        const child = spanMap.get(span.span_id);
        if (parent && child) {
          parent.children.push(child);
        }
      } else {
        const node = spanMap.get(span.span_id);
        if (node) rootSpans.push(node);
      }
    });

    // Layout tree
    const nodeWidth = 180;
    const nodeHeight = 60;
    const levelHeight = 100;
    const horizontalSpacing = 20;

    let positions = new Map<
      string,
      { x: number; y: number; node: SpanDetail & { children: SpanDetail[] } }
    >();
    let levelWidths = new Map();

    type TreeNode = SpanDetail & { children: TreeNode[] };
    const calculatePositions = (node: TreeNode, level = 0, offset = 0) => {
      if (!levelWidths.has(level)) {
        levelWidths.set(level, 0);
      }

      const x = offset + levelWidths.get(level);
      const y = 50 + level * levelHeight;

      positions.set(node.span_id, { x, y, node });

      let currentOffset = 0;
      (node.children || []).forEach((child) => {
        calculatePositions(child as TreeNode, level + 1, x + currentOffset);
        currentOffset += nodeWidth + horizontalSpacing;
      });

      levelWidths.set(level, levelWidths.get(level) + nodeWidth + horizontalSpacing);
    };

    rootSpans.forEach((root, idx) => {
      calculatePositions(root as unknown as TreeNode, 0, idx * (nodeWidth + horizontalSpacing));
    });

    // Draw connections
    ctx.strokeStyle = "#D1D5DB";
    ctx.lineWidth = 2;
    selectedTrace.spans.forEach((span) => {
      if (
        span.parent_span_id &&
        positions.has(span.parent_span_id) &&
        positions.has(span.span_id)
      ) {
        const parent = positions.get(span.parent_span_id);
        const child = positions.get(span.span_id);

        if (!parent || !child) return;

        ctx.beginPath();
        ctx.moveTo(parent.x + nodeWidth / 2, parent.y + nodeHeight);
        ctx.lineTo(child.x + nodeWidth / 2, child.y);
        ctx.stroke();

        // Arrow
        const angle = Math.atan2(child.y - parent.y - nodeHeight, child.x - parent.x);
        ctx.save();
        ctx.translate(child.x + nodeWidth / 2, child.y);
        ctx.rotate(angle + Math.PI / 2);
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(-5, -10);
        ctx.lineTo(5, -10);
        ctx.closePath();
        ctx.fillStyle = "#D1D5DB";
        ctx.fill();
        ctx.restore();
      }
    });

    // Draw nodes
    positions.forEach((pos) => {
      const span = pos.node;

      // Node color based on status and duration
      let fillColor = "#10b981"; // green
      if (span.status === "ERROR") {
        fillColor = "#ef4444"; // red
      } else if (span.duration_ms > 500) {
        fillColor = "#f59e0b"; // yellow
      }

      // Draw node rectangle
      ctx.fillStyle = fillColor;
      ctx.fillRect(pos.x, pos.y, nodeWidth, nodeHeight);

      // Border
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 2;
      ctx.strokeRect(pos.x, pos.y, nodeWidth, nodeHeight);

      // Service name
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 12px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(span.service_name.substring(0, 20), pos.x + nodeWidth / 2, pos.y + 25);

      // Duration
      ctx.font = "10px sans-serif";
      ctx.fillText(`${span.duration_ms.toFixed(2)}ms`, pos.x + nodeWidth / 2, pos.y + 45);
    });
  };

  const exportTrace = () => {
    if (!selectedTrace) return;

    const data = JSON.stringify(selectedTrace, null, 2);
    const blob = new Blob([data], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trace-${selectedTrace.trace_id}.json`;
    a.click();
  };

  const getCriticalPath = () => {
    if (!selectedTrace) return [] as SpanDetail[];

    // Find the longest path through the trace
    const spanMap = new Map<string, SpanDetail>();
    selectedTrace.spans.forEach((span) => {
      spanMap.set(span.span_id, span);
    });

    let criticalPath: SpanDetail[] = [];
    let maxDuration = 0;

    const findPath = (spanId: string, currentPath: SpanDetail[], currentDuration: number) => {
      const span = spanMap.get(spanId);
      if (!span) return;

      const newPath = [...currentPath, span];
      const newDuration = currentDuration + span.duration_ms;

      const children = selectedTrace.spans.filter((s) => s.parent_span_id === spanId);

      if (children.length === 0) {
        if (newDuration > maxDuration) {
          maxDuration = newDuration;
          criticalPath = newPath;
        }
      } else {
        children.forEach((child) => {
          findPath(child.span_id, newPath, newDuration);
        });
      }
    };

    const rootSpans = selectedTrace.spans.filter((s) => !s.parent_span_id);
    rootSpans.forEach((root) => findPath(root.span_id, [], 0));

    return criticalPath;
  };

  const filteredTraces = traces.filter((trace) => {
    const matchesSearch =
      trace.trace_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      trace.root_service.toLowerCase().includes(searchQuery.toLowerCase());

    if (filterStatus === "all") return matchesSearch;

    const hasErrors = trace.spans?.some((s) => s.status === "ERROR");
    if (filterStatus === "error") return matchesSearch && hasErrors;
    if (filterStatus === "ok") return matchesSearch && !hasErrors;

    return matchesSearch;
  });

  const criticalPath = selectedTrace ? getCriticalPath() : [];

  if (loading) {
    return (
      <div
        className="flex items-center justify-center h-96"
        data-testid="trace-visualization-loading"
      >
        <Network className="w-12 h-12 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="trace-visualization">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Distributed Trace Visualization</h2>
          <p className="text-gray-600 mt-1">
            Analyze service dependencies and performance bottlenecks
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchTraces} variant="outline" data-testid="refresh-traces-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={() => handleGenerateTrace(false)}
            data-testid="generate-normal-trace-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Normal Trace
          </Button>
          <Button
            onClick={() => handleGenerateTrace(true)}
            variant="destructive"
            data-testid="generate-anomaly-trace-btn"
          >
            <AlertCircle className="w-4 h-4 mr-2" />
            Anomalous Trace
          </Button>
          {selectedTrace && (
            <Button onClick={exportTrace} variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Trace List */}
        <Card className="lg:col-span-1" data-testid="trace-list-card">
          <CardHeader>
            <CardTitle>Traces ({filteredTraces.length})</CardTitle>

            {/* Search */}
            <div className="relative mt-2">
              <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search traces..."
                className="w-full pl-8 pr-3 py-2 border rounded text-sm"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Filter */}
            <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as any)}>
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="ok">OK Only</SelectItem>
                <SelectItem value="error">Errors Only</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredTraces.map((trace) => {
                const hasError = trace.spans?.some((s) => s.status === "ERROR");
                return (
                  <div
                    key={trace.trace_id}
                    onClick={() => loadTrace(trace.trace_id)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedTrace?.trace_id === trace.trace_id
                        ? "bg-blue-50 border-2 border-blue-300"
                        : "bg-gray-50 hover:bg-gray-100"
                    }`}
                    data-testid={`trace-item-${trace.trace_id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-xs font-mono text-gray-700 truncate flex-1">
                        {trace.trace_id}
                      </div>
                      {hasError && <AlertCircle className="w-4 h-4 text-red-500 ml-2" />}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">{trace.service_count} services</span>
                      <Badge variant="outline" className="text-xs">
                        {trace.cloud_provider}
                      </Badge>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {trace.total_duration_ms?.toFixed(2)}ms
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Visualization Area */}
        <Card className="lg:col-span-3" data-testid="graph-visualization-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                {selectedTrace ? `Trace: ${selectedTrace.trace_id}` : "Select a trace"}
              </CardTitle>
              {selectedTrace && (
                <div className="flex gap-2">
                  <Select value={viewMode} onValueChange={(v) => setViewMode(v as any)}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="graph">Graph View</SelectItem>
                      <SelectItem value="waterfall">Waterfall</SelectItem>
                      <SelectItem value="table">Table View</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {selectedTrace ? (
              <div>
                {/* Trace Info */}
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Root Service:</span>
                      <div className="font-medium">{selectedTrace.root_service}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Total Duration:</span>
                      <div className="font-medium">
                        {selectedTrace.total_duration_ms?.toFixed(2)}ms
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500">Services:</span>
                      <div className="font-medium">{selectedTrace.service_count}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Spans:</span>
                      <div className="font-medium">{selectedTrace.spans.length}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Cloud:</span>
                      <div className="font-medium">{selectedTrace.cloud_provider}</div>
                    </div>
                  </div>
                </div>

                {/* Critical Path */}
                {criticalPath.length > 0 && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center gap-2 text-sm">
                      <AlertCircle className="w-4 h-4 text-yellow-600" />
                      <span className="font-medium text-yellow-900">Critical Path:</span>
                      <span className="text-yellow-700">
                        {criticalPath.map((s) => s.service_name).join(" → ")}
                      </span>
                      <Badge className="bg-yellow-100 text-yellow-800 ml-auto">
                        {criticalPath.reduce((sum, s) => sum + s.duration_ms, 0).toFixed(2)}ms
                      </Badge>
                    </div>
                  </div>
                )}

                {/* Visualization */}
                {viewMode === "graph" && (
                  <div className="border rounded-lg overflow-hidden" style={{ height: "600px" }}>
                    <canvas ref={canvasRef} width={1200} height={600} className="w-full h-full" />
                  </div>
                )}

                {viewMode === "waterfall" && (
                  <div
                    className="border rounded-lg p-4"
                    style={{ maxHeight: "600px", overflowY: "auto" }}
                  >
                    <div className="space-y-1">
                      {selectedTrace.spans.map((span, idx) => {
                        const startOffset = 0; // In real impl, calculate from trace start
                        const maxDuration = selectedTrace.total_duration_ms || 1;
                        const widthPercent = (span.duration_ms / maxDuration) * 100;
                        const isCritical = criticalPath.some((s) => s.span_id === span.span_id);

                        return (
                          <div
                            key={span.span_id}
                            className="flex items-center gap-2 hover:bg-gray-50 p-2 rounded cursor-pointer"
                            onClick={() => {
                              setSelectedSpan(span);
                              setShowSpanDialog(true);
                            }}
                          >
                            <div className="w-32 text-xs font-medium truncate">
                              {span.service_name}
                            </div>
                            <div className="flex-1 h-6 bg-gray-100 rounded relative">
                              <div
                                className={`h-full rounded ${
                                  span.status === "ERROR"
                                    ? "bg-red-500"
                                    : isCritical
                                      ? "bg-yellow-500"
                                      : "bg-blue-500"
                                }`}
                                style={{ width: `${widthPercent}%` }}
                              >
                                <span className="text-xs text-white px-2 leading-6">
                                  {span.duration_ms.toFixed(1)}ms
                                </span>
                              </div>
                            </div>
                            <Badge
                              variant={span.status === "OK" ? "default" : "destructive"}
                              className={span.status === "OK" ? "bg-green-100 text-green-800" : ""}
                            >
                              {span.status}
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {viewMode === "table" && (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left">Service</th>
                          <th className="px-4 py-2 text-left">Operation</th>
                          <th className="px-4 py-2 text-left">Duration</th>
                          <th className="px-4 py-2 text-left">Status</th>
                          <th className="px-4 py-2 text-left">Kind</th>
                          <th className="px-4 py-2 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedTrace.spans.map((span) => (
                          <tr key={span.span_id} className="border-t hover:bg-gray-50">
                            <td className="px-4 py-2 font-medium">{span.service_name}</td>
                            <td className="px-4 py-2 text-gray-600">{span.name}</td>
                            <td className="px-4 py-2">{span.duration_ms.toFixed(2)}ms</td>
                            <td className="px-4 py-2">
                              <Badge
                                variant={span.status === "OK" ? "default" : "destructive"}
                                className={
                                  span.status === "OK" ? "bg-green-100 text-green-800" : ""
                                }
                              >
                                {span.status}
                              </Badge>
                            </td>
                            <td className="px-4 py-2">
                              <Badge variant="outline" className="text-xs">
                                {span.span_kind}
                              </Badge>
                            </td>
                            <td className="px-4 py-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedSpan(span);
                                  setShowSpanDialog(true);
                                }}
                              >
                                <Maximize2 className="w-3 h-3" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Legend */}
                <div className="mt-4 flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-green-500" />
                    <span>Healthy (&lt; 500ms)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-yellow-500" />
                    <span>Slow (&gt; 500ms) / Critical Path</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-red-500" />
                    <span>Error</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-96 flex items-center justify-center text-gray-500">
                Select a trace to visualize
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Span Details Dialog */}
      <Dialog open={showSpanDialog} onOpenChange={setShowSpanDialog}>
        <DialogContent className="max-w-2xl" data-testid="span-details-dialog">
          <DialogHeader>
            <DialogTitle>Span Details</DialogTitle>
          </DialogHeader>
          {selectedSpan && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-600">Span ID</div>
                  <div className="font-mono text-sm">{selectedSpan.span_id}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Trace ID</div>
                  <div className="font-mono text-sm">{selectedSpan.trace_id}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Service</div>
                  <div className="font-medium">{selectedSpan.service_name}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Operation</div>
                  <div className="font-medium">{selectedSpan.name}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Duration</div>
                  <div className="font-medium">{selectedSpan.duration_ms.toFixed(2)}ms</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Status</div>
                  <Badge
                    variant={selectedSpan.status === "OK" ? "default" : "destructive"}
                    className={selectedSpan.status === "OK" ? "bg-green-100 text-green-800" : ""}
                  >
                    {selectedSpan.status}
                  </Badge>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Span Kind</div>
                  <Badge variant="outline">{selectedSpan.span_kind}</Badge>
                </div>
                {selectedSpan.parent_span_id && (
                  <div>
                    <div className="text-sm text-gray-600">Parent Span</div>
                    <div className="font-mono text-xs">{selectedSpan.parent_span_id}</div>
                  </div>
                )}
              </div>

              <div>
                <div className="text-sm text-gray-600 mb-2">Timestamps</div>
                <div className="p-3 bg-gray-50 rounded text-xs space-y-1">
                  <div>Start: {new Date(selectedSpan.start_time).toISOString()}</div>
                  <div>End: {new Date(selectedSpan.end_time).toISOString()}</div>
                </div>
              </div>

              {selectedSpan.attributes && Object.keys(selectedSpan.attributes).length > 0 && (
                <div>
                  <div className="text-sm text-gray-600 mb-2">Attributes</div>
                  <div className="p-3 bg-gray-50 rounded text-xs">
                    <pre>{JSON.stringify(selectedSpan.attributes, null, 2)}</pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TraceVisualization;
